import {
  GridActionsCellItem,
  GridColumns,
  GridRenderCellParams,
  GridRowParams
} from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';
import {
  booleanFilterOperators,
  dateFilterOperators,
  filterOperators
} from '../../../../constants';
import { baseCell } from '../cells/base-cell';
import { isTenantAdministrator } from 'components/helpers/IsTenantAdministrator';
import FileUploader from 'components/common/file-uploader';
import { maintenanceService } from 'services/api';
import { compliantCell } from '../cells/compliant-cell';
import { DateSelectorDialog } from 'components/inventory/Ticketing/DateSelectorDialog';
import dayjs from 'dayjs';
import LockIcon from '@mui/icons-material/Lock';
import { isDirector } from 'components/helpers/IsDirector';
import ConfirmationDeleteTableDialog from 'components/helpers/ConfirmationGridDelete';
import { IsSuperAdminGuard } from 'components/helpers/IsSuperAdmin';
import { IMaintenance } from 'interfaces';

export const MaintenanceColumns = (
  openDialog: ((row: IMaintenance) => void) | null,
  detail = false,
  isArchive = false,
  isExpiration = false,
  handleDelete?: any
): GridColumns => {
  const translationPrefix = 'entities.equipment';
  const { t } = useTranslation();

  const isDirectorUser = isDirector();
  const viewReport = isTenantAdministrator() || isDirectorUser;
  const isSuperAdmin = IsSuperAdminGuard();

  const originalService = 'preemptive_maintenance';
  const columns = [];

  if (detail || isExpiration) {
    columns.push({
      field: 'equipment',
      headerName: t(`global.equipment`),
      width: 250,
      filterOperators,
      renderCell: baseCell
    });
    columns.push({
      field: 'description',
      headerName: t(`global.description`),
      width: 300,
      filterOperators,
      renderCell: baseCell
    });
    columns.push({
      field: 'serial_number',
      headerName: t(`entities.equipment.serial_number`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    });
    if (!isDirectorUser) {
      columns.push({
        field: 'warehouse_code',
        headerName: t(`global.warehouse`),
        width: 200,
        filterOperators,
        renderCell: baseCell
      });
    }
    columns.push({
      field: 'equipment_position',
      headerName: t('entities.equipment.position'),
      width: 200,
      filterOperators,
      sortable: false,
      valueGetter: (params) => params.row?.position ?? '',
      renderCell: baseCell
    });
    // columns.push({
    //   field: 'position',
    //   headerName: t(`entities.equipment.position`),
    //   width: 200,
    //   filterOperators,
    //   renderCell: baseCell
    // });
    columns.push({
      field: 'supplier',
      headerName: t(`global.supplier`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    });
    columns.push({
      field: 'assistance_supplier',
      headerName: t(`entities.equipment.assistance_supplier`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    });
  }

  [
    {
      field: 'expected_month',
      headerName: t(`entities.maintenance.expected_month`),
      width: 200,
      filterOperators: dateFilterOperators,
      renderCell: baseCell
    },
    {
      field: 'expected_date',
      headerName: t(`entities.maintenance.expected_date`),
      width: 200,
      filterOperators: dateFilterOperators,
      renderCell: (params: GridRenderCellParams) => {
        const maintenance = params.row;
        return (
          <div>
            <DateSelectorDialog
              dateField="expected_date"
              entityId={maintenance.id}
              initialDate={
                maintenance.expected_date ? dayjs(maintenance.expected_date, 'DD/MM/YYYY') : null
              }
              initialMonth={maintenance.expected_month}
            />
          </div>
        );
      }
    },
    {
      field: 'execution_date',
      headerName: t(`entities.maintenance.execution_date`),
      width: 200,
      filterOperators: dateFilterOperators,
      renderCell: (params: GridRenderCellParams) => {
        const maintenance = params.row;
        return (
          <div>
            {isSuperAdmin ? (
              <DateSelectorDialog
                dateField="execution_date"
                entityId={maintenance.id}
                initialDate={
                  maintenance.execution_date
                    ? dayjs(maintenance.execution_date, 'DD/MM/YYYY')
                    : null
                }
                initialMonth={null}
              />
            ) : (
              <span>{maintenance.execution_date}</span>
            )}
          </div>
        );
      }
    },
    {
      field: 'status',
      headerName: t(`global.status`),
      width: 220,
      filterOperators,
      valueGetter: (params) => {
        const displayName = params?.row?.status?.display_name;
        if (!displayName) return '';
        const maintenanceKey = `pages.maintenance.status_options.${displayName}`;
        const translated = t(maintenanceKey);
        if (translated !== maintenanceKey) return translated;
        return t(`pages.tickets.status_options.${displayName}`, {
          defaultValue: displayName
        });
      },
      renderCell: baseCell
    }
  ].forEach((column) => columns.push(column));

  // Aggiungi colonna compliant solo per archivio
  if (isArchive) {
    columns.push({
      field: 'compliant',
      headerName: t(`global.compliant`),
      width: 200,
      filterOperators: booleanFilterOperators,
      renderCell: compliantCell()
    });
  }

  columns.push(
    {
      field: 'notes',
      headerName: t(`global.notes`),
      width: 220,
      filterOperators,
      sortable: false,
      renderCell: baseCell
    },
    {
      field: 'technical_report',
      headerName: t(`${translationPrefix}.technical_report`),
      renderCell: (params: GridRenderCellParams) => {
        const maintenance = params.row;
        return (
          <div>
            {viewReport && (
              <FileUploader
                service={maintenanceService}
                entityId={maintenance.id}
                attachmentName={'technical_report'}
                filename={maintenance.technical_report}
                originalService={originalService}
                disableActions={isDirectorUser}
              />
            )}
          </div>
        );
      },
      width: 150,
      filterable: false,
      sortable: false
    },
    {
      field: 'checklist',
      headerName: t(`${translationPrefix}.checklist`),
      renderCell: (params: GridRenderCellParams) => {
        const maintenance = params.row;
        return (
          <div>
            {viewReport && (
              <FileUploader
                service={maintenanceService}
                entityId={maintenance.id}
                attachmentName={'checklist'}
                filename={maintenance.checklist}
                originalService={originalService}
              />
            )}
          </div>
        );
      },
      width: 150,
      filterable: false,
      sortable: false
    }
  );

  if (!isArchive && !isDirectorUser) {
    columns.push({
      field: 'actions',
      type: 'actions',
      headerName: t(`global.actions`),
      getActions: (params: GridRowParams) => {
        {
          return [
            <div key={1}>
              <GridActionsCellItem
                icon={<LockIcon />}
                onClick={() => {
                  openDialog?.(params.row as IMaintenance);
                }}
                label="Close"
              />
            </div>
          ];
        }
      },
      filterable: false
    });
  }
  if (isArchive && handleDelete && isSuperAdmin) {
    columns.push({
      field: 'actions',
      type: 'actions',
      headerName: t(`global.actions`),
      getActions: (params: GridRowParams) => {
        {
          return [
            <ConfirmationDeleteTableDialog
              key={`delete-${params.row.id}`}
              handleDelete={handleDelete}
              id={params.row.id}
            />
          ];
        }
      }
    });
  }

  return columns;
};
